import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, GlobalStyles } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeContext, useThemeStorage } from './hooks/useTheme';
import { useClarityTracking } from './hooks/useClarityTracking';
import { GOOGLE_OAUTH_CONFIG } from './config/oauth';
import AppRoutes from './routes';
import DynamicBackground from './components/common/DynamicBackground';
import ScrollToTop from './components/common/ScrollToTop';

// ============================================================================
// GLOBAL DYNAMIC BACKGROUND TOGGLE
// Set to true to enable animated background across all pages
// ============================================================================
const ENABLE_DYNAMIC_BACKGROUND = true;

// Global styles to make body transparent when dynamic background is enabled
const dynamicBackgroundStyles = (
  <GlobalStyles
    styles={{
      'html, body': {
        backgroundColor: 'transparent !important',
      },
      '#root': {
        backgroundColor: 'transparent',
        position: 'relative',
        zIndex: 1,
      },
    }}
  />
);

// Theme Provider Wrapper Component
const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const themeData = useThemeStorage();

  return (
    <ThemeContext.Provider value={themeData}>
      <MuiThemeProvider theme={themeData.theme}>
        <CssBaseline />
        {/* Global styles for transparent background */}
        {ENABLE_DYNAMIC_BACKGROUND && dynamicBackgroundStyles}
        {/* Global Dynamic Background - fixed position behind all content */}
        {ENABLE_DYNAMIC_BACKGROUND && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              zIndex: -1,
            }}
          >
            <DynamicBackground zIndex={0} opacity={1} blur={true} />
          </Box>
        )}
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Analytics Wrapper Component
const AnalyticsWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize Clarity tracking (user identification, page views)
  useClarityTracking();

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CONFIG.clientId}>
      <BrowserRouter>
        <ScrollToTop />
        <ThemeProviderWrapper>
          <AnalyticsWrapper>
            <AppRoutes />
          </AnalyticsWrapper>
        </ThemeProviderWrapper>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
};

export default App;